/**
 * Telegram provider types — DEV-878 (ADR-0030).
 *
 * Контракт фронта с messenger-providers/telegram. Общие ChatInfo/MessageInfo
 * берутся из messenger.types.ts, чтобы ChatWindow/MessageBubble рендерили
 * Telegram без изменений; здесь — только специфичные для Telegram формы
 * (сессия, QR-логин, WS-пейлоады) и мапперы вызываются в telegram.store.
 *
 * Идентичность пира — peer_key без access_hash: `user:{id}` | `chat:{id}` |
 * `channel:{id}` (ADR-0030). Он подставляется в общий ChatInfo.jid /
 * MessageInfo.chatJid как непрозрачный идентификатор.
 */

import type { ChatInfo, MessageInfo } from '@/features/messenger/types/messenger.types'

// ===================== Account / session =====================

/** Статус Telegram-аккаунта менеджера (зеркалит telegram_accounts.status). */
export type TelegramAccountStatus =
  | 'inactive' // аккаунт не подключён
  | 'pending_qr' // ждём скан QR
  | 'password_needed' // нужен 2FA cloud password
  | 'active' // подключён и работает
  | 'revoked' // сессия отозвана — нужен повторный вход
  | 'error' // ошибка runner'а

export interface TelegramStatusResponse {
  status: TelegramAccountStatus
  phone?: string
  username?: string
  firstName?: string
  lastName?: string
  /** ISO-время последнего успешного коннекта runner'а. */
  connectedAt?: string
}

// ===================== QR login flow =====================

/** Состояние клиентского флоу подключения (для модалки TG-M02, 6 состояний). */
export type TelegramLoginState =
  | 'idle' // модалка только открылась
  | 'loading' // запрашиваем QR / ждём ответ
  | 'qr' // QR показан, ждём скан
  | 'password' // отсканирован, требуется 2FA-пароль
  | 'success' // вход завершён
  | 'error' // ошибка (истёк QR, отказ и т.п.)

export interface TelegramStartLoginResponse {
  loginId: string
  /** tg://login?token=… — кодируется в QR. */
  qrText: string
  /** base64-PNG готового QR (бэкенд рисует), опционально. */
  imageBase64?: string
  expiresAt: string
}

export interface TelegramSubmitPasswordRequest {
  login_id: string
  password: string
}

/** DEV-1712. У общего аккаунта логин один, поэтому login_id в теле не нужен. */
export interface TelegramSupportSubmitPasswordRequest {
  password: string
}

export interface TelegramSubmitPasswordResponse {
  status: TelegramAccountStatus
  message?: string
}

export interface TelegramLogoutResponse {
  status: string
  message?: string
}

// ===================== Chats =====================

// DEV-1833: sentinel-владелец support-чата (manager_id чата и target_manager_id
// запросов). Значение совпадает с SUPPORT_LOGIN_ID (telegramSession), смысл другой.
export const SUPPORT_MANAGER_ID = 'support'

// DEV-1835: источник сообщения во фронтовой модели — 'me' (свой аккаунт),
// SUPPORT_MANAGER_ID или uuid чужого менеджера. В WS это `account_id ?? myId`.
export const OWN_SOURCE_ID = 'me'

/** Ключ открытого чата: источник + пир. peer_key один у всех аккаунтов клиента. */
export function buildChatKey(sourceManagerId: string, peerKey: string): string {
  return `${sourceManagerId}|${peerKey}`
}

/** tg_msg_id уникален только внутри (аккаунт, пир) — uid добавляет ключ чата. */
export function buildMessageUid(chatKey: string, messageId: string): string {
  return `${chatKey}|${messageId}`
}

/** Сообщение сегмента: общий MessageInfo + миллисекунды времени для слияния. */
export interface TelegramFeedMessage extends MessageInfo {
  sourceManagerId: string
  uid: string
  timestampMs: number
}

/** Один открытый чат стора: своя история, пагинация и chatInfo (DEV-1835). */
export interface ChatSegment {
  chatKey: string
  peerKey: string
  sourceManagerId: string
  messages: TelegramFeedMessage[]
  chatInfo: ChatInfo | null
  total: number
  /** Сколько сообщений уже получено REST'ом — offset следующей страницы. */
  offset: number
  hasMore: boolean
  isLoading: boolean
}

export interface TelegramChatInfo {
  peerKey: string
  accessHash?: string
  title: string
  phone?: string
  username?: string
  clientId?: string
  lastMessageTime: string
  unreadCount?: number
  createdAt: string
  updatedAt: string
  // DEV-998: менеджер-владелец чата — только в ответе getClientChats.
  managerId?: string
}

export interface TelegramChatsResponse {
  data: TelegramChatInfo[]
  // DEV-302: vinprop.common.v1.CursorPageInfo (total — int64 → строка в protojson)
  pagination: {
    nextPageToken?: string
    total?: number | string
  }
}

// DEV-998: чаты клиента по всем менеджерам (селектор кросс-просмотра).
export interface TelegramClientChatsResponse {
  data: TelegramChatInfo[]
}

// ===================== Messages =====================

export interface TelegramAttachmentInfo {
  id: string
  fileName: string
  fileUrl: string
  fileSize: number
  mimeType: string
  thumbnailUrl?: string
}

export interface TelegramMessageInfo {
  id: string
  peerKey: string
  senderTgId: string
  content: string
  timestamp: string
  isFromMe: boolean
  mediaType: string
  status: string
  isReadByManager: boolean
  replyToMsgId?: string
  editedAt?: string
  deletedAt?: string
  createdAt: string
  updatedAt: string
  attachments?: TelegramAttachmentInfo[]
  reactions?: TelegramReactionInfo[]
  // DEV-1835: аккаунт, под которым прочитана страница — uuid владельца или
  // 'support' (как account_id в WS). Нет поля = старый бэк.
  managerId?: string
}

export interface TelegramReactionInfo {
  emoji: string
  senderTgId: string
  isFromMe: boolean
}

export interface TelegramMessagesResponse {
  data: TelegramMessageInfo[]
  pagination: {
    nextPageToken?: string
    total?: number | string
  }
  chatInfo: TelegramChatInfo
}

// ===================== Send / actions =====================

export interface TelegramSendTextRequest {
  peer_key: string
  message: string
  reply_to_msg_id?: string
  client_id?: string
  // DEV-1833: отправка от имени поддержки — бэкенд принимает только 'support'.
  target_manager_id?: string
}

export interface TelegramSendImageRequest {
  peer_key: string
  image: string
  caption?: string
  client_id?: string
  target_manager_id?: string
}

export interface TelegramSendDocumentRequest {
  peer_key: string
  document: string
  file_name?: string
  client_id?: string
  target_manager_id?: string
}

/** Почему не ушло сообщение (DEV-2109): по коду карточка подбирает текст тоста. */
export interface TelegramSendFailure {
  code: 'not_connected' | 'unknown_peer' | 'flood' | 'generic'
  /** FLOOD_WAIT: через сколько секунд Telegram разрешит повторить. */
  seconds?: number
  /** Метка времени — два одинаковых провала подряд должны дать два тоста. */
  at: number
}

export interface TelegramSendMessageResponse {
  status: string
  messageId: string
  timestamp: string
}

export interface TelegramMarkAsReadRequest {
  peer_key: string
  message_id: string
  target_manager_id?: string
}

export interface TelegramReactRequest {
  peer_key: string
  message_id: string
  emoji: string
  target_manager_id?: string
}

export interface TelegramBindRequest {
  peer_key: string
  client_id: string
}

export interface TelegramActionResponse {
  status: string
  message?: string
}

// ===================== Calls (DEV-924 / DEV-925) =====================

export type TelegramCallDirection = 'incoming' | 'outgoing'

/** Статус транскрибации+AI записи (зеркалит telegram_calls.transcription_status). */
export type TelegramTranscriptionStatus = 'none' | 'pending' | 'processing' | 'completed' | 'failed'

/** AI-квалификация лида по разговору (phrases_json/ai_qualification_json — сырой JSON). */
export type TelegramCallQualificationLevel = 'hot' | 'warm' | 'cold'

export interface TelegramCallPhrase {
  speaker: 'manager' | 'client'
  text: string
}

export interface TelegramCallQualification {
  level?: TelegramCallQualificationLevel
  key_topics?: string[]
  next_steps?: string
  sentiment?: 'positive' | 'neutral' | 'negative'
}

/** Ответ GET /telegram/calls/{call_id}/details (grpc-gateway → camelCase). */
export interface TelegramCallDetailsResponse {
  callId: string
  direction: TelegramCallDirection
  state: string
  durationSec: number
  recordingUrl: string
  transcriptionStatus: TelegramTranscriptionStatus
  transcript?: string
  /** JSON-строка [{speaker, text}] — парсится на фронте. */
  phrasesJson?: string
  aiSummary?: string
  /** JSON-строка {level, key_topics, next_steps, sentiment}. */
  aiQualificationJson?: string
}

/** Сводка звонка в истории клиента (GET /telegram/calls, DEV-982). */
export interface TelegramCallSummary {
  callId: string
  direction: TelegramCallDirection
  state: string
  durationSec: number
  transcriptionStatus: TelegramTranscriptionStatus
  hasRecording: boolean
  /** RFC3339, пусто если не было. */
  startedAt?: string
  endedAt?: string
}

export interface TelegramListCallsResponse {
  calls?: TelegramCallSummary[]
}

// ===================== WS payloads (snake_case — собраны вручную на бэкенде) =====================

// account_id (DEV-1833, контракт DEV-1669): uuid менеджера-владельца аккаунта
// или 'support'. Нет поля = старый бэк, трактуем как событие своего аккаунта.

export interface WsTelegramNewMessagePayload {
  id: string
  peer_key: string
  sender_tg_id: string
  content: string
  timestamp: string
  is_from_me: boolean
  media_type: string
  title?: string
  account_id?: string
}

export interface WsTelegramMessageSentPayload {
  id: string
  peer_key: string
  content: string
  timestamp: string
  media_type: string
  account_id?: string
}

export interface WsTelegramMessageStatusPayload {
  id: string
  peer_key: string
  status: string
  account_id?: string
}

export interface WsTelegramMediaUpdatePayload {
  id: string
  peer_key: string
  media_type: string
  url?: string
  filename?: string
  file_length?: number
  account_id?: string
}

export interface WsTelegramReactionPayload {
  peer_key: string
  target_message_id: string
  sender_tg_id: string
  emoji: string
  is_from_me: boolean
  account_id?: string
}

export interface WsTelegramEditedPayload {
  id: string
  peer_key: string
  content: string
  edited_at: string
  account_id?: string
}

export interface WsTelegramDeletedPayload {
  id: string
  peer_key: string
  account_id?: string
}

export interface WsTelegramTypingPayload {
  peer_key: string
  sender_tg_id: string
  account_id?: string
}

export interface WsTelegramQrUpdatePayload {
  login_id: string
  qr_text: string
  image_base64?: string
  expires_at: string
}

export interface WsTelegramLoginStatusPayload {
  login_id?: string
  status: TelegramAccountStatus
  phone?: string
  username?: string
  message?: string
}

export interface WsTelegramAccountStatusPayload {
  /** Чей аккаунт: uuid менеджера или 'support' (DEV-2143). Нет поля — старый бэкенд, свой. */
  login_id?: string
  status: TelegramAccountStatus
  message?: string
}

/** Аккаунт в снимке PROVIDER_STATUS (DEV-1669) — те же данные, что отдаёт getStatus. */
export interface WsTelegramAccountSnapshot {
  account_id: string
  status: TelegramAccountStatus
  username?: string
  phone?: string
}

/**
 * Снимок статусов при подключении сокета и при смене прав (DEV-1669, DEV-2148).
 * Пустой блок — провайдер выключен или не ответил; WhatsApp и Zalo фронт отсюда не берёт.
 */
export interface WsProviderStatusPayload {
  telegram?: WsTelegramAccountSnapshot | null
  // Только при праве read_all.
  telegram_support?: WsTelegramAccountSnapshot | null
}
